import { useEffect, useRef, useState } from 'react';
import axios from 'axios';

// Completion comes from saved row/provenance data, never from a category's meaning.
export default function useCategorizationProgress({ phase, sessionId, API, data, editable, rows, pending, saveError, reset, refresh }) {
  const [failure, setFailure] = useState('');
  const [recovering, setRecovering] = useState(false);
  const retrying = useRef(false);
  const scope = useRef(null);
  if (scope.current?.id !== sessionId) scope.current = { id: sessionId, active: true };
  const current = scope.current;
  const latest = useRef();
  latest.current = { data, rows, reset, refresh };
  const ready = editable && data?.session_id === sessionId && Boolean(data?.submitted || data?.ai_suggestions_generated_at || data?.categorization_operation);
  const operation = data?.categorization_operation;
  const running = operation?.status === 'running' && Date.parse(operation.expires_at) > Date.now();
  const unavailable = rows.some(row => row.suggestion && !row.value);
  const operationFailed = operation?.status === 'failed' || (operation?.status === 'running' && !running);
  const error = failure || saveError || (ready && rows.length > 0 && !running && (unavailable || operationFailed)
    ? 'AI categorization could not finish. Already saved selections are preserved. Retry / check status, or select the remaining categories manually.' : '');
  const initial = useRef(false);
  if (ready && rows.length > 0) initial.current = true;
  if (!rows.length && pending === 0) initial.current = false;
  const active = ready && initial.current && !error;
  useEffect(() => {
    current.active = true; setFailure(''); setRecovering(false); retrying.current = false;
    return () => { current.active = false; };
  }, [current]);
  useEffect(() => {
    if (!active) return undefined;
    // A bounded UI wait does not cancel or start a server job. Recovery checks
    // the server's lease before considering another generation request.
    const timer = setTimeout(() => setFailure('Categorization is taking longer than expected. Saved selections are preserved. Retry / check status to reconcile the existing operation, or complete the remaining selections manually.'), 90000);
    return () => clearTimeout(timer);
  }, [active, sessionId]);
  const retry = async () => {
    if (retrying.current || pending > 0) return;
    retrying.current = true; setRecovering(true);
    const live = () => scope.current === current && current.active;
    try {
      const result = await axios.get(`${API}/sessions/${sessionId}/phase${phase}`, { timeout: 30000, skipGlobalLoading: true });
      if (!live()) return;
      const saved = result.data;
      const op = saved.categorization_operation;
      const isRunning = op?.status === 'running' && Date.parse(op.expires_at) > Date.now();
      // The backend also atomically arbitrates retries from simultaneous tabs.
      const empty = value => value == null || value === '';
      const needsInitial = phase === 3
        ? (saved.word_associations || []).some(w => (w.associations || []).some((_, i) => empty(w.classifications?.[i])))
        : (saved.selection_targets || []).some(t => t.coping_auto_eligible === true ||
          (t.coping_auto_eligible !== false && empty(t.coping_mechanism)) ||
          t.word_indices.some(i => Number.isInteger(i) && (t.domain_auto_eligible?.[i] === true ||
            (t.domain_auto_eligible?.[i] !== false && empty(t.thematic_domains?.[i])))));
      if (needsInitial && !isRunning && (!saved.ai_suggestions?.length || saved.ai_suggestions.some(s => s.ai_error))) {
        await axios.post(`${API}/sessions/${sessionId}/ai-suggestions/regenerate?phase=${phase}&background=true`, {}, { timeout: 30000, skipGlobalLoading: true });
      }
      if (!live()) return;
      const refreshed = await latest.current.refresh();
      if (!live()) return;
      if (refreshed === false) throw new Error('Saved state unavailable');
      latest.current.reset(); setFailure('');
    } catch (_) {
      if (live()) setFailure('Could not check categorization status. Saved selections are preserved. Check your connection and retry.');
    } finally { if (live()) { retrying.current = false; setRecovering(false); } }
  };
  return { active, error: ready ? error : saveError, recovering, retry };
}
